import type { QueryClient } from "@tanstack/react-query";
import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

const LETTER_REGEX = /[a-zA-Z]/;

const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "text-color": [
        "text-button-primary",
        "text-button-secondary",
        "text-button-link",
        "text-button-disbled",
        "text-button-warning",
        "text-button-info",
        "text-button-link-disabled",
      ],
      "font-size": [
        "text-body-xs",
        "text-body-sm",
        "text-body-md",
        "text-body-lg",
        "text-body-xl",
        "text-heading-sm",
        "text-heading-md",
        "text-heading-lg",
        "text-heading-xl",
        "text-heading-xxl",
        "text-heading-xxxl",
      ],
    },
    conflictingClassGroups: {
      "text-color": ["text-color"],
      "font-size": ["font-size"],
    },
  },
});
export function cn(...inputs: ClassValue[]) {
  return customTwMerge(clsx(inputs));
}

export const getCookieValue = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
  return null;
};

/**
 * Creates a person's initials from a full name string, or array containing
 * each part of a name.
 *
 * @param name - A string containing a full name, or an array of strings containing
 * each part of a name. i.e. "John Doe" or ["John", "Doe"]. Any string starting
 * with a non-letter character will be ignored.
 * @param options - Options for formatting the initials.
 * @param options.max - The maximum number of initials to return. If not provided,
 * the full initials will be returned. The first and last initials will always be
 * included (unless only 1 initial is requested), and the middle initials will be
 * returned up to the max number of initials.
 * @returns The person's initials.
 */
export const getInitials = (
  name: string | string[] | null,
  options?: { max?: number | null },
) => {
  if (!name) return "";

  let namePieces: string[] = [];

  if (Array.isArray(name)) {
    namePieces = name.map((n) => n.trim());
  } else {
    namePieces = name.split(" ").map((word) => word.trim());
  }

  const initials = namePieces
    .map((namePiece) => namePiece.slice(0, 1))
    .filter((piece) => LETTER_REGEX.test(piece))
    .join("")
    .toUpperCase();

  if (options?.max && options.max > 0) {
    if (initials.length <= options.max) {
      return initials;
    }

    if (options.max === 1) {
      return initials.slice(0, 1);
    }

    // Ensure we always pull out the first and last initial, then
    // pad the middle with remaining initials
    return (
      initials.slice(0, 1) +
      initials.slice(1, options.max - 1) +
      initials.slice(-1)
    );
  }

  return initials;
};

export const getGenderAbbreviation = (gender: string | null) => {
  if (!gender) return "";
  return gender.charAt(0).toUpperCase();
};

export const removeEmptyKeys = (params?: {
  [key: string]: string | number | undefined;
}) => {
  if (params) {
    return Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== undefined && value !== "",
      ),
    ) as { [key: string]: string };
  }
};

export const isProduction = () => import.meta.env.PROD;

export function cleanObject<T extends Record<string, unknown>>(
  obj: T,
): { [K in keyof T]?: T[K] } | undefined {
  const cleaned = Object.entries(obj).reduce(
    (acc, [key, value]) => {
      if (value === undefined) {
        return acc;
      }

      if (value && typeof value === "object" && !Array.isArray(value)) {
        const cleanedNested = cleanObject(value as Record<string, unknown>);
        if (cleanedNested && Object.keys(cleanedNested).length > 0) {
          acc[key as keyof T] = cleanedNested as T[keyof T];
        }
        return acc;
      }

      acc[key as keyof T] = value as T[keyof T];
      return acc;
    },
    {} as { [K in keyof T]?: T[K] },
  );

  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

export function wildcardInvalidateQuery(
  queryClient: QueryClient,
  queryKey: (string | undefined)[],
) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      if (query.queryKey.length === 0) return false;
      for (let i = 0; i < queryKey.length; i++) {
        const passedValue = queryKey[i];
        const existingValue = query.queryKey[i];

        if (passedValue !== "*" && passedValue !== existingValue) {
          return false;
        }
      }
      return true;
    },
  });
}

/**
 * Detects if text contains markdown formatting by checking for common markdown patterns.
 * This is used to determine whether to render content with MarkdownRenderer or plain text.
 *
 * Conservative approach: Only detects obvious markdown patterns to avoid false positives
 * with plain text that happens to start with dashes or numbers.
 *
 * @param text - The text to check for markdown patterns
 * @returns true if markdown patterns are detected, false otherwise
 */
export function hasMarkdownFormatting(text: string): boolean {
  if (!text || typeof text !== "string") return false;

  // Conservative markdown patterns - only detect the most obvious ones
  const markdownPatterns = [
    /\*\*[^*]+\*\*/g, // Bold text: **text**
    /\*[^*\s][^*]*\*/g, // Italic text: *text* (must have non-space after first *)
    /_[^_\s][^_]*_/g, // Italic text: _text_ (must have non-space after first _)
    /^#{1,6}\s+/m, // Headers: # Header
    /\[.+\]\(.+\)/g, // Links: [text](url)
    /`[^`]+`/g, // Inline code: `code`
    /^```/m, // Code blocks: ```
    /^\s*>\s+/m, // Blockquotes: > quote
    /^\s*---\s*$/m, // Horizontal rules: ---
    /~~[^~]+~~/g, // Strikethrough: ~~text~~
  ];

  // Check if any markdown pattern is found in the text
  return markdownPatterns.some((pattern) => pattern.test(text));
}
